import { toPlayback } from "~/features/detail/playback"
import type { DetailSession, DetailView } from "~/features/detail/types"
import type { GetDigitalEventByIdQuery } from "~/graphql/generated/graphql"
import { formatDate, formatDuration, formatMetaLine, progressPercent } from "~/shared/lib/formatting"

type DigitalEvent = NonNullable<GetDigitalEventByIdQuery["digitalEventByID"]>
type EventDay = NonNullable<DigitalEvent["days"]>[number]
type EventSession = EventDay["sessions"][number]

const UNTITLED_EVENT = "Untitled event"

/**
 * A digital event's playable video hangs off a per-feed source rather than the
 * session itself. Only the standard feed is requested — ASL, Spanish, and
 * open-caption feeds are a player concern, not a detail-page one.
 */
const standardVideoOf = (session: EventSession) => session.onDemandSources?.standard?.video

const durationMillisecondsOf = (session: EventSession): number | undefined => {
  const sources = standardVideoOf(session)?.videoSources

  return sources?.__typename === "VideoSources" ? (sources.durationMilliseconds ?? undefined) : undefined
}

const toSession = (eventId: string, session: EventSession, index: number): DetailSession => {
  const video = standardVideoOf(session)
  const durationMilliseconds = durationMillisecondsOf(session)

  return {
    id: session.id,
    title: session.name?.trim() || video?.title?.trim() || `Session ${index + 1}`,
    description: session.description?.trim() || video?.description?.trim() || undefined,
    thumbnailUrl: video?.thumbnailUrl ?? undefined,
    duration: formatDuration(durationMilliseconds),
    progressPercent: progressPercent(video?.progress?.playheadSeconds, durationMilliseconds),
    href: `/event/${eventId}/session/${session.id}`,
    playback: toPlayback(video),
  }
}

/** Days are ordered; flattening them keeps the running order of the event. */
const flattenSessions = (event: DigitalEvent): readonly EventSession[] =>
  (event.days ?? []).flatMap(day => day.sessions)

const lastPlayedIndex = (sessions: readonly EventSession[]): number => {
  let best = -1
  let bestTime = 0

  sessions.forEach((session, index) => {
    const lastUpdate = standardVideoOf(session)?.progress?.lastUpdate
    const time = lastUpdate ? Date.parse(lastUpdate) : Number.NaN

    if (!Number.isNaN(time) && time > bestTime) {
      best = index
      bestTime = time
    }
  })

  return best
}

export const toEventView = (event: DigitalEvent): DetailView => {
  const rawSessions = flattenSessions(event)
  const sessions = rawSessions.map((session, index) => toSession(event.id, session, index))
  const resumeIndex = lastPlayedIndex(rawSessions)
  const startIndex = resumeIndex >= 0 ? resumeIndex : 0

  return {
    id: event.id,
    title: event.name?.trim() || UNTITLED_EVENT,
    metaLine: formatMetaLine([formatDate(event.startDate), event.ministryArea ?? undefined]),
    description: event.description?.trim() || undefined,
    backdropUrl: event.posterUrl ?? event.thumbnailUrl ?? sessions[0]?.thumbnailUrl,
    sessions,
    startHref: sessions[startIndex]?.href,
    hasProgress: resumeIndex >= 0,
  }
}
